import {Map, source} from "azure-maps-control";
import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzurePolygonLayer} from "@esWebAPI/es/map/layer/azure/AzurePolygonLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {ProgressStatus, TerritoryLayerWorkerBroker} from "@esWebAPI/es/map/layer/territory/TerritoryLayerWorkerBroker";
import TerritoryModel, {ITerritoryActions, ITerritoryFeature} from "@esWebAPI/es/map/TerritoryModel";
import {AzureLineLayer} from "@esWebAPI/es/map/layer/azure/AzureLineLayer";
import {TerritoryLayerUtils} from "@esWebAPI/es/map/layer/territory/TerritoryLayerUtils";
import * as geojson from "geojson";
import {AzureTerritorySelectionLayer} from "@esWebAPI/es/map/layer/azure/AzureTerritorySelectionLayer";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import {InCriteria, NoneCriteria} from "@esWebAPI/es/model/Criteria";
import {AzureCriteriaAdapter} from "@esWebAPI/es/util/AzureCriteriaAdapter";
import {TerritoryColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import {ITerritoryBaseLayer, TerritoryBaseFeatureWorker} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import { ExpressionFilterSpecification } from "@maplibre/maplibre-gl-style-spec";

export class TerritoryBaseLayer extends MapLayer implements ITerritoryBaseLayer {

    private readonly _polygonLayer: AzurePolygonLayer;
    private readonly _lineLayer: AzureLineLayer;
    private readonly _selectionLayer: AzureTerritorySelectionLayer;
    private readonly _territoryModel: TerritoryModel;
    private readonly worker: TerritoryLayerWorkerBroker;

    private readonly colorStyler: TerritoryColorThematicStyler;

    private datasource: source.Source|undefined;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                territoryModel: TerritoryModel,
                eventChannel: IEventChannel) {

        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        let styler = stylerFactory.getColorStyler(layerView);
        this.colorStyler = styler  as TerritoryColorThematicStyler;

        this._polygonLayer = new AzurePolygonLayer(map, layerView, datasourceManager, styler);
        this._lineLayer = new AzureLineLayer(map, layerView, datasourceManager, styler);
        this._selectionLayer = new AzureTerritorySelectionLayer(map, spriteManager)
        this.worker = new TerritoryLayerWorkerBroker(territoryModel);
        this._territoryModel = territoryModel;


        this.initListeners();

        this.createLayers().then(() => {
            this.onReady.resolve();
        });
    }

    private initListeners() {
        this.eventChannel.addListener("onTerritoryChange", this.onTerritoryChange, this);
        this.eventChannel.addListener("onTerritoryLevelChange", this.onTerritoryLevelChange, this);
        this.eventChannel.addListener("onTerritoryFilterChange", this.onTerritoryFilterChange, this);
        this.territoryModel.getSelectionModel().getEventChannel().addListener("onSelectionChange", this.onSelectionChange, this);
    }

    private async createLayers() {
        this.datasource = await this.datasourceManager.create(this.layerView, this.layerInfo, false);
        await this._polygonLayer.create(this.datasource);
        await this._lineLayer.create(this.datasource);
        await this._selectionLayer.create(this.datasource);


        const progressListener = (status: ProgressStatus) => {
            this.eventChannel.fireEvent("onTerritoryBaseLayerLoadProgress", status);
        };
        await this.worker.create(this.layerView, {}, progressListener);
        this.eventChannel.fireEvent("onTerritoryLayerLoadFinish", this.layerView);

    }

    private onSelectionChange() {
        const selectionModel = this.territoryModel.getSelectionModel();
        if (!selectionModel.isActive()) {
            const criteria: NoneCriteria = { "$type": "None" };
            const filter = AzureCriteriaAdapter.convert(criteria, this.map);
            this._selectionLayer.setFilter(filter);
            return;
        }

        let selectedBaseFeatureIds: number[] = [];
        if (selectionModel.getMode() === "BASE") {
            selectedBaseFeatureIds = selectionModel.getBaseFeatIds();
        } else {
            selectedBaseFeatureIds = selectionModel.getAllBaseFeatsInUpperLevelBaseCache();
        }

        const criteria: InCriteria<number> = { "$type": "In", attr: "__id", values: selectedBaseFeatureIds };
        const filter = AzureCriteriaAdapter.convert(criteria, this.map);
        this._selectionLayer.setFilter(filter);
    }

    private async onTerritoryLevelChange() {
        // Hide all features in the Selection Layer
        const criteria: NoneCriteria = { "$type": "None" };
        const filter = AzureCriteriaAdapter.convert(criteria, this.map);
        this._selectionLayer.setFilter(filter);
        this.colorStyler.update();
        await Promise.all([
            this._polygonLayer.update(this.layerView),
            this._lineLayer.update(this.layerView),
        ]);
    }

    private onTerritoryChange(_actions: ITerritoryActions, _territories: ITerritoryFeature[], _changesTerritoryIds: number[]) {
        this.colorStyler.update();
        this._polygonLayer.update(this.layerView);
        this._lineLayer.update(this.layerView);
    }

    private async onTerritoryFilterChange() {
        this.colorStyler.update();
        await Promise.all([
            this._polygonLayer.update(this.layerView),
            this._lineLayer.update(this.layerView),
        ]);
    }

    public getLayerIds(): string[] {
        return [this._polygonLayer.getLayerId(), this._lineLayer.getLayerId()];
    }

    public get territoryModel(): TerritoryModel {
        return this._territoryModel;
    }

    public get featureWorker(): TerritoryBaseFeatureWorker {
        return this.worker;
    }

    public isRelatedExtraPointLayer() {
        return false;
    }

    public isOutlinesLayer() {
        return false;
    }

    public isRelatedLayer() {
        return false;
    }

    public isBaseLayer() {
        return true;
    }

    public createLabel(feature: geojson.Feature) {
        return TerritoryLayerUtils.createLabel(this.layerView, feature);
    }

    public destroy(): void {
        super.destroy();

        this.eventChannel.removeListener("onTerritoryChange", this.onTerritoryChange);
        this.eventChannel.removeListener("onTerritoryLevelChange", this.onTerritoryLevelChange);
        this.eventChannel.removeListener("onTerritoryFilterChange", this.onTerritoryFilterChange);
        this.territoryModel.getSelectionModel().getEventChannel().removeListener("onSelectionChange", this.onSelectionChange);

        this._polygonLayer.remove();
        this._lineLayer.remove();
        this._selectionLayer.remove();
        //this._territoryModel = null;

        if (this.datasource) {
            this.datasourceManager.remove(this.datasource.getId());
        }

        this.worker.destroy();
    }

    protected getColorThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected getSymbolThematicFilter(): ExpressionFilterSpecification | undefined {
        return undefined;
    }

    protected hideFeature(_featureId: number): void {
    }

    protected showFeatures(): void {
    }

    public update(_layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {
        return Promise.resolve(undefined);
    }

    public getActiveAzureLayerId(): string | undefined {
        return this._polygonLayer.getLayerId();
    }
}